import { useCallback, useId, useMemo, useState } from 'react'
import { StepProps } from '../../types'
import { hasField, isFieldRequired, isFieldSatisfied, getFieldMaxLength } from '../../schema-utils'
import { FormFloatingFooter } from '@Components/claim-flow/form-floating-footer'
import { Button } from '@Components/button/button'
import { Selector } from '@Components/selector/selector'
import { PreviousButton } from '@Components/previous-button/previous-button'

import './contact-step.scss'

const CLASS_NAME = 'contact-step'
const SECTION = 'contact'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const ContactStep = ({
  schema,
  geoData,
  formData,
  onSubmit,
  onBack,
  showBack = true,
}: StepProps) => {
  const existing = (formData.contact ?? {}) as Record<string, unknown>
  const fieldId = useId()
  const claimant = (formData.claimant ?? {}) as { countryOfResidence?: string; usCitizen?: boolean }

  const isUS = claimant.usCitizen !== false
  const selectedCountry = isUS ? 'United States' : (claimant.countryOfResidence ?? 'United States')

  const regionOptions = useMemo(() => {
    const country = geoData.countries.find((c) => c.name === selectedCountry)
    if (!country) {
      return []
    }
    return country.regions.map((r) => ({
      label: r,
      value: r,
    }))
  }, [geoData, selectedCountry])

  const [mailingAddress1, setMailingAddress1] = useState((existing.mailingAddress1 as string) ?? '')
  const [mailingAddress2, setMailingAddress2] = useState((existing.mailingAddress2 as string) ?? '')
  const [city, setCity] = useState((existing.city as string) ?? '')
  const [region, setRegion] = useState((existing.region as string) ?? '')
  const [postalCode, setPostalCode] = useState((existing.postalCode as string) ?? '')
  const [emailAddress, setEmailAddress] = useState((existing.emailAddress as string) ?? '')
  const [emailTouched, setEmailTouched] = useState(false)

  const formatPhoneNumber = useCallback((value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 10)
    const match = digits.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/)

    if (!match) {
      return ''
    }
    const [, area, prefix, line] = match
    if (line) {
      return `(${area}) ${prefix}-${line}`
    }
    if (prefix) {
      return `(${area}) ${prefix}`
    }
    if (area) {
      return `(${area}`
    }
    return ''
  }, [])

  const [mobilePhone, setMobilePhone] = useState(
    existing.mobilePhoneNumber ? formatPhoneNumber(existing.mobilePhoneNumber as string) : '',
  )
  const [secondaryPhone, setSecondaryPhone] = useState(
    existing.secondaryContactNumber
      ? formatPhoneNumber(existing.secondaryContactNumber as string)
      : '',
  )

  const isEmailValid = emailAddress === '' || EMAIL_REGEX.test(emailAddress)

  const isValid =
    isFieldSatisfied(schema, SECTION, 'mailingAddress1', mailingAddress1) &&
    isFieldSatisfied(schema, SECTION, 'city', city) &&
    isFieldSatisfied(schema, SECTION, 'region', region) &&
    isFieldSatisfied(schema, SECTION, 'postalCode', postalCode) &&
    isFieldSatisfied(schema, SECTION, 'mobilePhoneNumber', mobilePhone) &&
    isEmailValid

  const handleSubmit = useCallback(() => {
    if (!isValid) {
      return
    }

    const contact: Record<string, unknown> = {}

    if (hasField(schema, SECTION, 'mailingAddress1')) {
      contact.mailingAddress1 = mailingAddress1
    }
    if (hasField(schema, SECTION, 'mailingAddress2') && mailingAddress2) {
      contact.mailingAddress2 = mailingAddress2
    }
    if (hasField(schema, SECTION, 'city')) {
      contact.city = city
    }
    if (hasField(schema, SECTION, 'region')) {
      contact.region = region
    }
    if (hasField(schema, SECTION, 'postalCode')) {
      contact.postalCode = postalCode
    }
    if (hasField(schema, SECTION, 'mobilePhoneNumber')) {
      contact.mobilePhoneNumber = mobilePhone
    }
    if (hasField(schema, SECTION, 'secondaryContactNumber') && secondaryPhone) {
      contact.secondaryContactNumber = secondaryPhone
    }
    if (hasField(schema, SECTION, 'emailAddress') && emailAddress) {
      contact.emailAddress = emailAddress
    }

    onSubmit({ contact })
  }, [
    isValid,
    schema,
    mailingAddress1,
    mailingAddress2,
    city,
    region,
    postalCode,
    mobilePhone,
    secondaryPhone,
    emailAddress,
    onSubmit,
  ])

  const regionLabel = isUS ? 'State' : 'Province/Region'

  return (
    <section className={CLASS_NAME}>
      <h2 className={`${CLASS_NAME}__section-title`}>Contact Information</h2>

      <section className={`${CLASS_NAME}__row`}>
        {hasField(schema, SECTION, 'mailingAddress1') && (
          <section className={`${CLASS_NAME}__field`}>
            <label htmlFor={`${fieldId}-mailing-address-1`} className={`${CLASS_NAME}__label`}>
              Mailing Address 1 {isFieldRequired(schema, SECTION, 'mailingAddress1') && '*'}
            </label>
            <input
              id={`${fieldId}-mailing-address-1`}
              type="text"
              autoComplete="address-line1"
              value={mailingAddress1}
              onChange={(e) => setMailingAddress1(e.target.value)}
              maxLength={getFieldMaxLength(schema, SECTION, 'mailingAddress1')}
              placeholder="Street address"
              className={`${CLASS_NAME}__input-control`}
            />
          </section>
        )}
        {hasField(schema, SECTION, 'mailingAddress2') && (
          <section className={`${CLASS_NAME}__field`}>
            <label htmlFor={`${fieldId}-mailing-address-2`} className={`${CLASS_NAME}__label`}>
              Mailing Address 2
            </label>
            <input
              id={`${fieldId}-mailing-address-2`}
              type="text"
              autoComplete="address-line2"
              value={mailingAddress2}
              onChange={(e) => setMailingAddress2(e.target.value)}
              maxLength={getFieldMaxLength(schema, SECTION, 'mailingAddress2')}
              placeholder="Apt, suite, unit, etc."
              className={`${CLASS_NAME}__input-control`}
            />
          </section>
        )}
      </section>

      <section className={`${CLASS_NAME}__row`}>
        {hasField(schema, SECTION, 'city') && (
          <section className={`${CLASS_NAME}__field`}>
            <label htmlFor={`${fieldId}-city`} className={`${CLASS_NAME}__label`}>
              City {isFieldRequired(schema, SECTION, 'city') && '*'}
            </label>
            <input
              id={`${fieldId}-city`}
              type="text"
              autoComplete="address-level2"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              maxLength={getFieldMaxLength(schema, SECTION, 'city')}
              className={`${CLASS_NAME}__input-control`}
            />
          </section>
        )}

        {hasField(schema, SECTION, 'region') && (
          <section className={`${CLASS_NAME}__field`}>
            <span id={`${fieldId}-region`} className={`${CLASS_NAME}__label`}>
              {regionLabel} {isFieldRequired(schema, SECTION, 'region') && '*'}
            </span>
            <Selector
              ariaLabelledBy={`${fieldId}-region`}
              autoComplete="address-level1"
              placeHolderText="Select..."
              selectables={regionOptions}
              value={region || undefined}
              onValueChange={setRegion}
            />
          </section>
        )}

        {hasField(schema, SECTION, 'postalCode') && (
          <section className={`${CLASS_NAME}__field`}>
            <label htmlFor={`${fieldId}-postal-code`} className={`${CLASS_NAME}__label`}>
              Postal Code {isFieldRequired(schema, SECTION, 'postalCode') && '*'}
            </label>
            <input
              id={`${fieldId}-postal-code`}
              type="text"
              autoComplete="postal-code"
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
              maxLength={getFieldMaxLength(schema, SECTION, 'postalCode')}
              className={`${CLASS_NAME}__input-control`}
            />
          </section>
        )}
      </section>

      {hasField(schema, SECTION, 'mobilePhoneNumber') && (
        <section className={`${CLASS_NAME}__field`}>
          <label htmlFor={`${fieldId}-mobile-phone`} className={`${CLASS_NAME}__label`}>
            Mobile Phone Number {isFieldRequired(schema, SECTION, 'mobilePhoneNumber') && '*'}
          </label>
          <input
            id={`${fieldId}-mobile-phone`}
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            value={mobilePhone}
            onChange={(e) => setMobilePhone(formatPhoneNumber(e.target.value))}
            placeholder="(123) 456-7890"
            maxLength={14}
            className={`${CLASS_NAME}__input-control`}
          />
        </section>
      )}

      {hasField(schema, SECTION, 'secondaryContactNumber') && (
        <section className={`${CLASS_NAME}__field`}>
          <label htmlFor={`${fieldId}-secondary-phone`} className={`${CLASS_NAME}__label`}>
            Secondary Contact Number
          </label>
          <input
            id={`${fieldId}-secondary-phone`}
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            value={secondaryPhone}
            onChange={(e) => setSecondaryPhone(formatPhoneNumber(e.target.value))}
            placeholder="(123) 456-7890"
            maxLength={14}
            className={`${CLASS_NAME}__input-control`}
          />
        </section>
      )}

      {hasField(schema, SECTION, 'emailAddress') && (
        <section className={`${CLASS_NAME}__field`}>
          <label htmlFor={`${fieldId}-email`} className={`${CLASS_NAME}__label`}>
            Email Address {isFieldRequired(schema, SECTION, 'emailAddress') && '*'}
          </label>
          <input
            id={`${fieldId}-email`}
            type="email"
            autoComplete="email"
            value={emailAddress}
            onChange={(e) => setEmailAddress(e.target.value)}
            onBlur={() => setEmailTouched(true)}
            maxLength={getFieldMaxLength(schema, SECTION, 'emailAddress')}
            placeholder="email@example.com"
            className={`${CLASS_NAME}__input-control`}
          />
          {emailTouched && !isEmailValid && (
            <span className={`${CLASS_NAME}__field-error`}>Please enter a valid email address</span>
          )}
        </section>
      )}

      <FormFloatingFooter>
        <section className="floating-footer__buttons">
          <PreviousButton show={showBack} onClick={onBack} />
          <Button
            className="floating-footer__next-button"
            disabled={!isValid}
            onClick={handleSubmit}>
            Next
          </Button>
        </section>
      </FormFloatingFooter>
    </section>
  )
}
